import React, { useState, useEffect } from "react";
import {
  DataGridPro,
  GridRowModes,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid-pro";
import { Box, Button, Select, MenuItem, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import EditShareholder from "../Modal/EditShareholder";
import AssociateShareholder from "../Modal/AssociateShareholder";
import ModifieShareholder from "../Modal/ModifieShareholder";
import { CustomAlert } from "../../Common/Components/CustomAlert";

/* ---------------------- Custom Toolbar ---------------------- */
function CustomToolbar({ onAddRow, onAssociateShareholder }) {
  const { t } = useTranslation(["Company"]);

  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        startIcon={<AddIcon />}
        onClick={onAddRow}
        sx={{ textTransform: "none", mr: 1 }}
      >
        Add Shareholder
      </Button>

      <Button
        color="secondary"
        startIcon={<ManageAccountsIcon />}
        onClick={onAssociateShareholder}
        sx={{ textTransform: "none", mr: 1 }}
      >
        {t("AddExistingShareholder")}
      </Button>

      {/* 🔥 Nouveaux boutons Pro */}
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />

      <GridToolbarQuickFilter debounceMs={500} />
    </GridToolbarContainer>
  );
}

/* -------------------- Custom Cell Editor -------------------- */
function ActiveEditCell(props) {
  const { id, field, value, api } = props;

  const handleChange = (event) => {
    api.setEditCellValue({ id, field, value: event.target.value });
  };

  return (
    <Select value={value ?? false} onChange={handleChange} fullWidth size="small">
      <MenuItem value={true}>Actif</MenuItem>
      <MenuItem value={false}>Inactif</MenuItem>
    </Select>
  );
}

/* ------------------------ Main Component ------------------------ */
function ShareholderList({ company, shareHoldersByEmplacement, refreshShareholders }) {
  const userState = useSelector((state) => state.user);
  const { t } = useTranslation(["Company"], ["Common"]);

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [shareHolders, setShareHolders] = useState([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openAssociateShareholder, setOpenAssociateShareholder] = useState(false);

  const [openModifieShareholder, setOpenModifieShareholder] = useState(false);
  const [shareholderToModify, setShareholderToModify] = useState(null);

  /* ---------------- Modal Handlers ---------------- */
  const handleOpenAddModal = () => setOpenAddModal(true);
  const handleCloseAddModal = () => setOpenAddModal(false);

  const handleOpenAssociateShareholder = () => setOpenAssociateShareholder(true);
  const handleCloseAssociateShareholder = () => setOpenAssociateShareholder(false);

  const handleOpenModifieShareholder = (row) => {
    setShareholderToModify(row);
    setOpenModifieShareholder(true);
  };
  const handleCloseModifieShareholder = () => {
    setShareholderToModify(null);
    setOpenModifieShareholder(false);
  };

  /* ---------------- Load initial data ---------------- */
  useEffect(() => {
    GetShareHolders();
  }, []);

  useEffect(() => {
    if (shareHoldersByEmplacement?.length) {
      BuildRows(shareHoldersByEmplacement);
    }
  }, [shareHoldersByEmplacement]);

  const BuildRows = (data) => {
    if (!data || !data.length) return;
    const newRows = data.map((e, i) => ({
      id: i,
      idDepartement: e.idDepartement,
      noPersonne: e.noPersonne,   // ✅ AJOUTER ICI
      noEmploye: e.noEmploye,
      isActive: !e.estInactif,
      firstName: e.firstname,
      lastName: e.lastName,
      username: e.username,
      email: e.email,
      NoCellulaire: e.noCellulaire,
      langue: e.language,
      department: e.department?.textEn ?? "",
      lastLogin: e.lastLogin,
      PourcentageAction: e.pourcentageAction,
      estOperateur: e.estInactif,
    }));
    setRows(newRows);
  };

  /* ---------------- Save new shareholder ---------------- */
  const handleSaveNewShareholder = async (newShareholder) => {
    try {
      handleCloseAddModal();
      await GetShareHolders();
      setAlertMessage("Shareholder ajouté avec succès !");
      setAlertType("success");
      setAlertOpen(true);
    } catch (err) {
      setAlertMessage("Erreur lors de l'ajout du shareholder.");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  /* ---------------- Columns ---------------- */
  const columns = [
    {
      field: "noPersonne",
      headerName: "No Personne",
      width: 150,
      hide: true, // ✅ cachée
    },
    {
      field: "noEmploye",
      headerName: "No Employe",
      width: 150,
      hide: true, // ✅ cachée
    },
    {
      field: "idDepartement",
      headerName: "Id Departement",
      width: 50,
      hide: true, // 👈 invisible
    },
    {
      field: "isActive",
      headerName: "Statut",
      width: 120,
      editable: false,
      renderCell: (params) => (
        <span style={{ color: params.row.isActive ? "green" : "red" }}>
          {params.row.isActive ? "Actif" : "Inactif"}
        </span>
      ),
    },
    { field: "firstName", headerName: "Prenom", width: 130 },
    { field: "lastName", headerName: "Nom", width: 130 },
    { field: "username", headerName: "Username", width: 130 },
    { field: "email", headerName: "Email", width: 250, filterable: true },
    { field: "NoCellulaire", headerName: "No Cellulaire", width: 150, editable: true },
    { field: "langue", headerName: "Langue", width: 100 },
    {
      field: "department",
      headerName: "Departement",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {params.row.estOperateur && (
            <Tooltip title="Opérateur">
              <StarIcon sx={{ color: "gold", fontSize: 18 }} />
            </Tooltip>
          )}
          {params.value}
        </Box>
      ),
    },
    { field: "PourcentageAction", headerName: "% Action", width: 100, editable: true },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon color="primary" />}
          label="Modifier"
          onClick={() => handleOpenModifieShareholder(params.row)}
        />,
      ],
    },
  ];

  /* ---------------- Row Edit Handlers ---------------- */
  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((r) => (r.id === newRow.id ? updatedRow : r)));
    handleSaveShareholder(updatedRow);
    return updatedRow;
  };

  /* ---------------- API calls ---------------- */
  const GetShareHolders = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/employee/GetShareHolders`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
      });
      const data = await res.json();
      setShareHolders(data);
      
    } catch (error) {
      setAlertMessage("Error fetching shareholders");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  const handleSaveShareholder = async (shareholder) => {
    const shareholderDTO = {
      FirstName: shareholder.firstName,
      LastName: shareholder.lastName,
      Username: shareholder.username,
      Email: shareholder.email,
      Langue: shareholder.langue,
      NoCellulaire: shareholder.NoCellulaire,
      PourcentageAction: shareholder.PourcentageAction,
      EstInactif: !shareholder.isActive,
      noCompany: company.noCompagnie,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/employee/SaveShareholder`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
        body: JSON.stringify(shareholderDTO),
      });

      const result = await response.json();
      setAlertMessage(result === "echec" ? "Failed to save shareholder" : "Shareholder saved successfully");
      setAlertType(result === "echec" ? "error" : "success");
      setAlertOpen(true);
    } catch (error) {
      setAlertMessage("Error saving shareholder");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  /* ---------------- Fetch address ---------------- */
  const GetAdresse = async (row) => {
    const dataShareholder = { username: row.username };
    try {
      const res = await fetch(`employe/GetAdresse`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userState.token}`,
        },
        body: JSON.stringify(dataShareholder),
      });
      const data = await res.json();
      console.log("Adresse shareholder :", data);
    } catch (error) {
      setAlertMessage("Error fetching address");
      setAlertType("error");
      setAlertOpen(true);
    }
  };

  return (
    <>
      <CustomAlert open={alertOpen} message={alertMessage} typeMessage={alertType} closeMessage={() => setAlertOpen(false)} />

   <DataGridPro
  editMode="row"
  rows={rows}
  columns={columns}
  rowModesModel={rowModesModel}
  onRowModesModelChange={setRowModesModel}
  onRowEditStop={handleRowEditStop}
  processRowUpdate={processRowUpdate}

  /* ---------------- Toolbar ---------------- */
  slots={{
    toolbar: CustomToolbar,
  }}
  slotProps={{
    toolbar: {
      onAddRow: handleOpenAddModal,
      onAssociateShareholder: handleOpenAssociateShareholder,
      showQuickFilter: true,
      quickFilterProps: { debounceMs: 500 },
    },
  }}

  /* ---------------- Pagination ---------------- */
  pagination
  pageSizeOptions={[5, 10, 25, 50]}
  initialState={{
    pagination: {
      paginationModel: { pageSize: 5, page: 0 },
    },
    filter: {
      filterModel: {
        items: [],
      },
    },
  }}

  /* ---------------- Filtering ---------------- */
  filterMode="client"
  disableColumnFilter={false}
  disableMultipleColumnsFiltering={false}

  /* ---------------- Selection ---------------- */
  disableRowSelectionOnClick

  /* ---------------- Appearance ---------------- */
  autoHeight
  density="standard"
  sx={{
    m: 2,
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f5f5f5",
      fontWeight: "bold",
    },
  }}
/>

      <EditShareholder open={openAddModal} handleClose={handleCloseAddModal} onSave={handleSaveNewShareholder} company={company} shareholders={rows} initialData={{}} />

      <AssociateShareholder open={openAssociateShareholder} handleClose={handleCloseAssociateShareholder} company={company} onAssociate={refreshShareholders} />

      {shareholderToModify && (
        <ModifieShareholder
          open={openModifieShareholder}
          handleClose={handleCloseModifieShareholder}
          shareholder={shareholderToModify}
          company={company}
          initialData={{}}
          onSave={async (data, isError) => {
            if (isError) {
              setAlertMessage(t("shareholder.update.error"));
              setAlertType("error");
              setAlertOpen(true);
              return;
            }

            await refreshShareholders();
            handleCloseModifieShareholder();

            setAlertMessage(t("shareholder.update.success"));
            setAlertType("success");
            setAlertOpen(true);
          }}
        />
      )}
    </>
  );
}

export default ShareholderList;
